import { Box, ThemeProvider } from '@mui/material';
import { useParams } from 'react-router-dom';
import invariant from 'tiny-invariant';
import { useQuote } from '@services/quotes';

import Loading from '@components/Loading';
import { useComposeTheme } from '@theme/theme';

import { MessageDrawerItem } from '@pages/QuoteDetails/sidebarItems/messages/Messages';
import { FullscreenQuoteDetails } from './FullscreenQuoteDetails';
import { Panel } from '@components/panel';
import { TechnicalDetailsPanel } from '@pages/QuoteDetails/sidebarItems/tools/TechnicalDetailsModal';
import { UnderwritingRulesAndIssuesTab } from '@pages/UnderwriterToolkit/tab-content/UnderwritingRulesAndIssuesTab';
import { QuoteSummaryTab } from '@pages/UnderwriterToolkit/tab-content/QuoteSummaryTab/QuoteSummaryTab';
import { WIDTH_CONSTANTS } from '@theme/widthConstants';
import { SubjectivitiesTable } from './tab-content/SubjectivitiesTable';
import { DrawerList } from '@pages/QuoteDetails/constants/DrawerList.enum';
import { useAlertStore } from '@store/AlertStore';
import { useQuoteStore } from '@store/Quote/QuoteStore';
import { QuoteDrawerUrlParams } from '@pages/QuoteDetails/constants/drawerListItems';
import { NotesDialogContent } from '@components/notesDialog/NotesDialogContent';
import { DrawerHeader } from '@components/drawerItem';
import { RelatedTransactions } from '@pages/QuoteDetails/sidebarItems/relatedTransactions';

export const UnderwriterToolkitContent = () => {
  const { quoteId } = useParams<'quoteId'>();
  invariant(quoteId);
  const lightTheme = useComposeTheme('light');
  const { quote } = useQuote(quoteId);
  const { headerHeight } = useAlertStore();

  const [selectedLayoutTab, panelInstances] = useQuoteStore((s) => [s.selectedLayoutTab, s.panelInstances]);

  const activeTab = selectedLayoutTab ?? DrawerList.QuoteDetails;

  if (!quote || !panelInstances || panelInstances.length === 0) {
    return (
      <ThemeProvider theme={lightTheme}>
        <Box
          sx={{
            display: 'flex',
            flexGrow: 1,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Loading />
        </Box>
      </ThemeProvider>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'quoteDetails':
        return <FullscreenQuoteDetails quote={quote} />;
      case QuoteDrawerUrlParams.Summary:
        return (
          <Panel color='primary' sx={panelStyles}>
            <QuoteSummaryTab />
          </Panel>
        );
      case QuoteDrawerUrlParams.Subjectivities:
        return (
          <Panel color='primary' sx={panelStyles}>
            <SubjectivitiesTable />
          </Panel>
        );
      case QuoteDrawerUrlParams.Underwriting:
        return (
          <Panel color='primary' sx={panelStyles}>
            <UnderwritingRulesAndIssuesTab />
          </Panel>
        );
      case QuoteDrawerUrlParams.Tools:
        return (
          <Panel color='primary' sx={panelStyles}>
            <TechnicalDetailsPanel isDialogMode={false} quoteId={quoteId} />
          </Panel>
        );
      case QuoteDrawerUrlParams.Messages:
        return (
          <Panel color='primary' sx={{ ...panelStyles, padding: 0 }}>
            <MessageDrawerItem isFullScreen={true} />
          </Panel>
        );
      case QuoteDrawerUrlParams.Notes:
        return (
          <Panel color='primary' sx={{ ...panelStyles, padding: 0, overflow: 'hidden' }}>
            <DrawerHeader drawerTitle='Notes' />
            <NotesDialogContent quoteId={quoteId} context='quote-detail' customerId={quote.customer.id} />
          </Panel>
        );
      case QuoteDrawerUrlParams.RelatedTransactions:
        return (
          <Panel color='primary' sx={{ ...panelStyles, padding: 0, overflow: 'hidden' }}>
            <RelatedTransactions isFullScreen={true} />
          </Panel>
        );
      default:
        return <div>Unknown tab: {activeTab}</div>;
    }
  };

  const panelStyles = {
    padding: { xs: 0, sm: 2 },
    width: '100%',
    maxWidth: { md: WIDTH_CONSTANTS.md, lg: WIDTH_CONSTANTS.lg, xl: WIDTH_CONSTANTS.xl },
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: '300px',
    maxHeight: `calc(100vh - ${headerHeight}px)`, // Account for header and padding
    overflow: 'hidden', // Prevent panel overflow
  };

  return (
    <ThemeProvider theme={lightTheme}>
      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          justifyContent: 'center',
          overflow: 'hidden', // Prevent overflow, let panels handle their own scrolling
          padding: activeTab === DrawerList.QuoteDetails ? 0 : 2,
          paddingBottom: activeTab === DrawerList.QuoteDetails ? 0 : 4,
          height: '100%', // Take full available height
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: '100%', // Take full available height
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
          }}
        >
          {renderTabContent()}
        </Box>
      </Box>
    </ThemeProvider>
  );
};
